import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from '../../../config';
import { decodeResponse, validateResponse } from './responseUtil';
import { prepareHeaders } from './utils';
import { ServiceOrderPageCodec } from './types/serviceOrder/serviceOrdersApi.types';
import { mapToServiceOrders } from './mappers/serviceOrder/serviceOrdersMapper';
import {
    ServiceOrderBulkUpdatePreview,
    ServiceOrderPage,
    ServiceOrderStatus,
} from '../serviceOrders/serviceOrders/serviceOrder.types';
import {
    ServiceOrderCompletion,
    ServiceOrderStartInfo,
} from '../serviceOrders/serviceOrders/serviceOrderExecution.types';
import { mapToServiceOrderExcelQueryParameters } from './mappers/serviceOrder/serviceOrderQueryParamsMapper';
import { TableSort } from '../sharedComponents/table/SortedTableHeader';
import { ServiceOrdersTableColumns } from '../serviceOrders/serviceOrders/table/ServiceOrdersTableColumns';
import { ServiceType } from '../serviceOrders/shared/commonServiceOrder.types';
import { FileExport } from '../sharedComponents/common.types';
import { FILE_NAME_REGEX } from './transportOrderApi';
import {
    mapToApiServiceOrderCompletion,
    mapToApiServiceOrderStartInfos,
} from './mappers/serviceOrder/serviceOrderExecutionMapper';
import {
    mapToServiceOrderFilterQueryParameters,
    mapToServiceOrderQueryBody,
} from './mappers/serviceOrder/requestBodyMappers';
import {
    ServiceOrderBulkSelectionCodec,
    ServiceOrderBulkUpdatePreviewCodec,
} from './types/serviceOrder/serviceOrderBulkUpdateApi.types';
import {
    mapToServiceOrderBulkUpdatePreview,
    mapToServiceOrderIds,
} from './mappers/serviceOrder/serviceOrderBulkUpdateMapper';

export const SERVICE_ORDERS_PATH = 'service-orders';
export const SERVICE_ORDER_IDS_PATH = 'service-order-ids';
export const BULK_UPDATE_PREVIEW_SERVICE_ORDERS_PATH = 'bulk-update-preview-service-orders';
export const EXPORT_PATH = 'exports';
export const START_INFOS_PATH = 'start-infos';
export const COMPLETIONS_PATH = 'completions';
export const HAS_UNACKNOWLEDGED_CHANGES_PATH = 'has-unacknowledged-external-changes';

export const ARCHIVED_PATH = 'is-archived';

export const CONTENT_TYPE_EXCEL = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export type GetServiceOrdersQueryParams = GetServiceOrdersFilterQueryParams & {
    sortBy?: TableSort<ServiceOrdersTableColumns>;
    limit: number;
};

export type GetServiceOrdersFilterQueryParams = {
    location?: string;
    statuses?: ServiceOrderStatus[];
    serviceTypes?: ServiceType[];
    orderIssuedAtAfter?: Date;
    orderIssuedAtBefore?: Date;
    archived?: boolean;
    vins?: string[] | undefined;
    externalOrderId?: string;
    externalOptionId?: string;
};

export type GetServiceOrderExcelQueryParams = {
    location?: string;
    statuses?: ServiceOrderStatus[];
    serviceTypes?: ServiceType[];
    orderIssuedAtAfter?: Date;
    orderIssuedAtBefore?: Date;
};

export type PutServiceOrderStartInfoProps = {
    serviceOrderId: string;
    startInfo: ServiceOrderStartInfo;
};

export type PutServiceOrderCompletionProps = {
    serviceOrderId: string;
    completion: ServiceOrderCompletion;
};

type PutArchiveServiceOrderProps = {
    serviceOrderId: string;
};

type PutAcknowledgeServiceOrderChangesProps = {
    serviceOrderId: string;
};

type PostServiceOrderBulkUpdatePreviewProps = {
    orderIds: string[];
};

export const serviceOrderTag = 'ServiceOrder';

export const serviceOrderApi = createApi({
    reducerPath: 'serviceOrderApi',
    baseQuery: fetchBaseQuery({
        baseUrl: config.backend.FINISHED_VEHICLES_SERVICE,
        prepareHeaders,
    }),
    tagTypes: [serviceOrderTag],
    // eslint-disable-next-line max-lines-per-function
    endpoints: (builder) => ({
        getServiceOrders: builder.query<ServiceOrderPage, GetServiceOrdersQueryParams>({
            query: (queryParameters) => ({
                url: SERVICE_ORDERS_PATH,
                method: 'POST',
                body: mapToServiceOrderQueryBody(queryParameters),
                validateStatus: validateResponse(ServiceOrderPageCodec),
            }),
            transformResponse: decodeResponse(ServiceOrderPageCodec, mapToServiceOrders),
            providesTags: [serviceOrderTag],
        }),
        getServiceOrderIds: builder.mutation<string[], GetServiceOrdersFilterQueryParams>({
            query: (queryParameters) => ({
                url: SERVICE_ORDER_IDS_PATH,
                method: 'POST',
                body: mapToServiceOrderFilterQueryParameters(queryParameters),
                validateStatus: validateResponse(ServiceOrderBulkSelectionCodec),
            }),
            transformResponse: decodeResponse(ServiceOrderBulkSelectionCodec, mapToServiceOrderIds),
        }),
        postServiceOrderBulkUpdatePreview: builder.mutation<
            ServiceOrderBulkUpdatePreview,
            PostServiceOrderBulkUpdatePreviewProps
        >({
            query: ({ orderIds }) => ({
                url: BULK_UPDATE_PREVIEW_SERVICE_ORDERS_PATH,
                method: 'POST',
                body: { order_ids: orderIds },
                validateStatus: validateResponse(ServiceOrderBulkUpdatePreviewCodec),
            }),
            transformResponse: decodeResponse(ServiceOrderBulkUpdatePreviewCodec, mapToServiceOrderBulkUpdatePreview),
        }),
        getServiceOrderExcel: builder.mutation<FileExport, GetServiceOrderExcelQueryParams>({
            query: (queryParameters) => ({
                url: `${SERVICE_ORDERS_PATH}/${EXPORT_PATH}`,
                params: mapToServiceOrderExcelQueryParameters(queryParameters),
                cache: 'no-cache',
                responseHandler: async (response): Promise<FileExport> => {
                    if (response.headers.get('content-type') === CONTENT_TYPE_EXCEL) {
                        return {
                            fileLink: window.URL.createObjectURL(await response.blob()),
                            fileName: response.headers.get('Content-Disposition')?.match(FILE_NAME_REGEX)?.at(1),
                        };
                    } else {
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                        return response.json();
                    }
                },
            }),
        }),
        postServiceOrderStartInfos: builder.mutation<void, PutServiceOrderStartInfoProps>({
            query: ({ serviceOrderId, startInfo: executionDetails }) => ({
                url: `${SERVICE_ORDERS_PATH}/${serviceOrderId}/${START_INFOS_PATH}`,
                method: 'POST',
                body: mapToApiServiceOrderStartInfos(executionDetails),
            }),
            invalidatesTags: [serviceOrderTag],
        }),
        postServiceOrderCompletions: builder.mutation<void, PutServiceOrderCompletionProps>({
            query: ({ serviceOrderId, completion: executionDetails }) => ({
                url: `${SERVICE_ORDERS_PATH}/${serviceOrderId}/${COMPLETIONS_PATH}`,
                method: 'POST',
                body: mapToApiServiceOrderCompletion(executionDetails),
            }),
            invalidatesTags: [serviceOrderTag],
        }),
        putArchiveServiceOrder: builder.mutation<void, PutArchiveServiceOrderProps>({
            query: (props) => ({
                url: `${SERVICE_ORDERS_PATH}/${props.serviceOrderId}/${ARCHIVED_PATH}`,
                method: 'PUT',
                body: 'true',
            }),
            invalidatesTags: [serviceOrderTag],
        }),
        putAcknowledgeServiceOrderChanges: builder.mutation<void, PutAcknowledgeServiceOrderChangesProps>({
            query: (props) => ({
                url: `${SERVICE_ORDERS_PATH}/${props.serviceOrderId}/${HAS_UNACKNOWLEDGED_CHANGES_PATH}`,
                method: 'PUT',
                body: 'false',
            }),
            invalidatesTags: [serviceOrderTag],
        }),
    }),
});

export const {
    useGetServiceOrdersQuery,
    useGetServiceOrderIdsMutation,
    usePostServiceOrderBulkUpdatePreviewMutation,
    useGetServiceOrderExcelMutation,
    usePostServiceOrderStartInfosMutation,
    usePostServiceOrderCompletionsMutation,
    usePutAcknowledgeServiceOrderChangesMutation,
    usePutArchiveServiceOrderMutation,
} = serviceOrderApi;

export const matchServiceOrderReceived = serviceOrderApi.endpoints.getServiceOrders.matchFulfilled;
